import DatePicker from "@/components/date-picker";
import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { RequestTypes } from "@/constants/enums";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/grain-requests/future-contract/schema/handler-functions";
import { GranoEntity } from "@/pages/grain-requests/sell-orders/schema/sell-order.schema";
import { getListadoGranos } from "@/services/aux-entities.service";
import { getStatusByEntity } from "@/services/entity-status.service";

import { AppColors } from "@/styles/colors";
import {
  onlyAlphanumericAndSpaces,
  onlyNumbers,
} from "@/utils/reg-expressions";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Dayjs } from "dayjs";
import { Filter, SearchIcon } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  loadingOptions?: boolean;
  maxDate?: Dayjs;
  disableApply?: boolean;
}

export const SlotRequestFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  loadingOptions,
  maxDate,
  disableApply,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  //handleSearch,
  clearFilters,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [loadingGranos, setLoadingGranos] = useState<boolean>(false);

  const [granos, setGranos] = useState<GranoEntity[] | null>([]);

  const fetchGranos = async () => {
    setLoadingGranos(true);

    const response = await getListadoGranos();
    setGranos(response);

    setLoadingGranos(false);
  };

  const [statusOptions, setStatusOptions] = useState<
    { nombre: string; id: number }[]
  >([]);
  const [loadingStatusOptions, setLoadingStatusOptions] =
    useState<boolean>(false);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: 2,
      IdTipoSolicitud: RequestTypes.Cupos,
    });

    setStatusOptions(response);
    setLoadingStatusOptions(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchGranos();
      fetchStatusOptions();
    }
  }, [isActive]);

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems={"center"} justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha"
                    name="slot_request_date"
                    placeholder="Ingresar fecha"
                    maxDate={maxDate}
                  />
                </GridItem>

                <GridItem maxWidth="250px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Titular cuenta</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="account_number"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="origin"
                    labelText="Procedencia"
                    placeholder="Ingresar procedencia"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={onlyAlphanumericAndSpaces}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="estados"
                    items={statusOptions.map((status) => ({
                      label: status.nombre,
                      value: status.id,
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control}
                    loading={loadingStatusOptions}
                    disabled={loadingStatusOptions}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="plant_slots"
                    labelText="Cupos a planta"
                    placeholder="Ingresar cant. de cupos"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={onlyNumbers}
                  />
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="direct_slots"
                    labelText="Cupos directos"
                    placeholder="Ingresar cant. de cupos"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={onlyNumbers}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="grain"
                    items={
                      granos?.map((g) => ({
                        label: g.nombre,
                        value: g.id,
                      })) || []
                    }
                    labelText="Grano"
                    placeholder="Seleccionar"
                    control={control}
                    loading={loadingOptions || loadingGranos}
                    disabled={loadingGranos}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters || disableApply}
                data-testid="counter-delivery-apply-filters-btn"
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
